import { useCallback } from 'react'

import { useMutation } from '@apollo/client'
import {
  ExpensablesDataTableDocument,
  MyGroupedExpensablesDataTableDocument,
} from 'generated/resources/datatable.graphql'
import {
  CreateGermanyPerDiemDocument,
  CreateGermanyPerDiemMutation,
  CreateGermanyPerDiemMutationVariables,
  ExpensablesDataCollectionDocument,
  ExpensablesStatusCountsDocument,
  GetExpensesCountDocument,
  GetGroupsDocument,
  MyGroupedExpensablesDataCollectionDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import type {
  CostCenterAssignment,
  DayExclusion,
  Destination,
  PerDiemWizardData,
} from 'modules/expenses/components/expenses/New/PerDiemWizard/form/types'

interface CreatePerDiemResult {
  success: boolean
  expensableId?: number
  perDiemId?: number
  error?: string
}

interface UseCreateGermanyPerDiemReturn {
  createPerDiem: (data: PerDiemWizardData, companyId: number) => Promise<CreatePerDiemResult>
  isCreating: boolean
}

const formatDateToISO = (date: Date | null): string | undefined => {
  if (!date) return undefined
  return date.toISOString()
}

const formatDateToISODate = (date: Date): string => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate()
  ).padStart(2, '0')}`
}

const mapDestinations = (destinations: Destination[]) => {
  return destinations
    .filter((dest): dest is Destination & { arrivalDatetime: Date } =>
      Boolean(dest.locationData.country && dest.locationName && dest.arrivalDatetime)
    )
    .map((dest) => {
      const { arrivalDatetime } = dest
      return {
        locationName: dest.locationName,
        arrivalDate: arrivalDatetime.toISOString(),
        country: dest.locationData.country,
        administrativeAreaLevel1: dest.locationData.administrativeAreaLevel1,
        administrativeAreaLevel2: dest.locationData.administrativeAreaLevel2,
        locality: dest.locationData.locality,
      }
    })
}

const mapConcepts = (dayExclusions: DayExclusion[]) => {
  return dayExclusions.map((exclusion) => ({
    date: formatDateToISODate(exclusion.date),
    breakfastProvided: exclusion.breakfastProvided,
    lunchProvided: exclusion.lunchProvided,
    dinnerProvided: exclusion.dinnerProvided,
    accommodationProvided: exclusion.accommodationProvided,
  }))
}

const mapProjects = (projectId?: number, subprojectId?: number) => {
  if (!projectId) return undefined

  return [
    {
      projectWorkerId: projectId,
      subprojectId: subprojectId ?? undefined,
    },
  ]
}

const mapCostCenters = (assignments: CostCenterAssignment[], startDate: Date | null) => {
  if (assignments.length === 0) return undefined

  const dateStr = startDate?.toISOString().split('T')[0] ?? new Date().toISOString().split('T')[0]

  return {
    assignments: {
      assignmentsToCreate: assignments.map((assignment) => ({
        costCenterId: assignment.costCenterId,
        // Backend expects percentage as decimal (0.0-1.0), frontend stores as 0-100
        percentage: assignment.percentage / 100,
        startDate: assignment.startDate ?? dateStr,
      })),
      assignmentsToUpdate: [],
    },
  }
}

export const useCreateGermanyPerDiem = (): UseCreateGermanyPerDiemReturn => {
  const { employee } = useCurrentSession()

  const [mutate, { loading }] = useMutation<
    CreateGermanyPerDiemMutation,
    CreateGermanyPerDiemMutationVariables
  >(CreateGermanyPerDiemDocument, {
    refetchQueries: [
      ExpensablesDataTableDocument,
      ExpensablesDataCollectionDocument,
      ExpensablesStatusCountsDocument,
      GetExpensesCountDocument,
      GetGroupsDocument,
      MyGroupedExpensablesDataTableDocument,
      MyGroupedExpensablesDataCollectionDocument,
    ],
    awaitRefetchQueries: true,
  })

  const createPerDiem = useCallback(
    async (data: PerDiemWizardData, companyId: number): Promise<CreatePerDiemResult> => {
      try {
        const destinations = mapDestinations(data.destinations)

        if (destinations.length === 0) {
          return {
            success: false,
            error: 'At least one valid destination is required',
          }
        }

        const projects = mapProjects(data.projectId, data.subprojectId)
        const costCenters = mapCostCenters(data.costCenterAssignments, data.startDate)

        const result = await mutate({
          variables: {
            companyId,
            employeeId: data.employeeId ?? employee.id,
            description: data.description,
            startDate: formatDateToISO(data.startDate),
            endDate: formatDateToISO(data.endDate),
            destinations,
            concepts: mapConcepts(data.dayExclusions),
            files: data.files,
            budgetId: data.budgetId,
            internalReference: data.internalReference,
            projects,
            costCenters,
            currency: 'EUR',
          },
        })

        const expensable = result.data?.expensesMutations?.expensable

        if (!expensable?.expensable) {
          const errors = expensable?.errors
          const firstError = errors?.[0]
          const errorMessage =
            (firstError && 'message' in firstError ? firstError.message : null) ??
            'Failed to create per diem'
          return {
            success: false,
            error: errorMessage,
          }
        }

        return {
          success: true,
          expensableId: expensable.expensable.id,
          perDiemId: expensable.expensable.perDiem?.id,
        }
      } catch (error) {
        return {
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error occurred',
        }
      }
    },
    [mutate, employee.id]
  )

  return {
    createPerDiem,
    isCreating: loading,
  }
}
